import { translations } from "@/i18n/translations";

// Shared provenance vocabulary + per-actor capabilities (Phase 2/3/4 portals).
//
// The label maps below are kept in French and used by the ADMIN screens, which
// are intentionally French-only. The public/pro surfaces translate the same
// vocabulary through the i18n `prov` block instead (t(`prov.events.${type}`)
// etc.), so pro components no longer import these maps.
//
// Event types have a single dictionary (RDEC-011): the admin reads its French
// half rather than keeping a copy that drifts.
export const EVENT_LABELS = translations.fr.prov.events;

export const ACTOR_LABELS = {
  proprietaire: "Propriétaire",
  createur: "Créateur",
  horloger: "Horloger",
  marchand: "Marchand",
  expert: "Expert",
  assureur: "Assureur",
  admin: "Provena",
};

export const LEVEL_LABELS = {
  SELF_REPORTED: "Déclaré par le propriétaire",
  DOCUMENTED: "Déclaré, pièce à l'appui",
  ATELIER_REPORTED: "Signalé par un atelier",
  CREATOR_DECLARED: "Déclaré par le créateur",
  PROFESSIONAL: "Professionnel enregistré",
  VERIFIED: "Consigné par Provena",
  MANUFACTURER_VERIFIED: "Confirmé par la manufacture",
};

// Verification ladder styling (ADR-005), shared by the public timeline, the pro
// portal and the admin table so a new degree is styled in one place.
//
// Three families of speech, three inks (ADR-005, second amendment; RDEC-013).
// The owner's own entries stay grey. Gold marks an identified third party — a
// maker, a professional, a manufacture — and nothing else. What Provena records
// gets a neutral ink of its own: it is not an outside witness. The families
// themselves are defined once, server-side (`verification.FAMILY`); a test holds
// these styles to them. The label always carries the meaning, never the colour
// alone.
export const LEVEL_CLS = {
  SELF_REPORTED: "border-ink/15 text-ink-muted",
  DOCUMENTED: "border-ink/25 text-ink-body",
  ATELIER_REPORTED: "border-gold/30 text-gold-ink",
  CREATOR_DECLARED: "border-gold/50 text-gold-ink",
  PROFESSIONAL: "border-gold/50 text-gold-ink",
  VERIFIED: "border-ink/60 bg-ink/[0.06] text-ink",
  MANUFACTURER_VERIFIED: "border-gold/70 text-gold-ink",
};

export const LEVEL_TEXT_CLS = {
  SELF_REPORTED: "text-ink-muted",
  DOCUMENTED: "text-ink-body",
  ATELIER_REPORTED: "text-gold-ink",
  CREATOR_DECLARED: "text-gold-ink",
  PROFESSIONAL: "text-gold-ink",
  VERIFIED: "text-ink",
  MANUFACTURER_VERIFIED: "text-gold-ink",
};

// A level written by another version must never borrow a stronger degree's
// styling — it reads as the weakest one, same rule as `verification.rank`.
export const levelClass = (level, map = LEVEL_CLS) =>
  map[level] || map.SELF_REPORTED;

// Actor types a pro can apply as. Labels/descriptions live in i18n
// (prov.proActors.*), resolved at the usage site (ProApplyPage).
// Acquisition methods, in the order the register form offers them. Mirrors
// `ACQUISITION_METHODS` in schemas_watch.py — the server remains the authority;
// this list only decides what the UI proposes. Labels live in i18n
// (registerForm.acquisition.*).
export const ACQUISITION_VALUES = [
  "neuf_boutique",
  "neuf_createur",
  "occasion_particulier",
  "occasion_plateforme",
  "heritage",
  "autre",
];

export const PRO_ACTOR_VALUES = ["createur", "horloger", "marchand", "expert", "assureur"];

// A maker opens dossiers for the pieces they made and still hold. This gate is
// about MAKING, not holding: it also guards recording a fabrication, which only
// a maker does. Holding stock is a different question — see holdsOwnStock.
export const registersOwnPieces = (user) =>
  !!user && user.pro_status === "approved" && user.actor_type === "createur";

// Qui peut détenir une montre en propre ? Un créateur détient les pièces qu'il
// a faites, un marchand son stock jusqu'à la vente : tous deux peuvent donc
// ouvrir un dossier à leur nom.
//
// Mais un marchand fait aussi du dépôt-vente, où la montre reste au client. On
// ne le devine donc jamais : on le lui demande. Se tromper ne produirait pas
// une gêne d'affichage — `owner_name`, `owner_email` et `owner_country` entrent
// dans le sceau, et le registre scellerait une propriété fausse.
//
// Un horloger, un expert ou un assureur n'ont jamais la montre en propre : la
// question ne leur est pas posée.
export const holdsOwnStock = (user) =>
  !!user &&
  user.pro_status === "approved" &&
  ["createur", "marchand"].includes(user.actor_type);

// Le dossier qu'un professionnel ouvre à son propre nom ne se raconte pas
// pareil selon ce qu'il est : un créateur a fait la pièce, un marchand l'a
// achetée pour la revendre. Même mécanique, deux vérités différentes.
//
// Le mode nomme ce geste partout où il doit l'être — libellés, mesure, canal
// d'enregistrement — pour qu'un marchand ne soit jamais compté comme un atelier.
export const ownStockMode = (actorType) =>
  actorType === "createur" ? "atelier" : "stock";

export const ownStockCopyKey = (actorType) => `${ownStockMode(actorType)}Register`;

// Event types each actor may create from the portal.
export const ACTOR_EVENT_TYPES = {
  createur: ["CREATION", "SERVICE", "SALE"],
  horloger: ["SERVICE", "APPRAISAL"],
  // Un dépôt-vente lie un propriétaire et un marchand : lui seul, parmi les
  // professionnels, a quelque chose à y consigner (ADR-008). Ni l'un ni l'autre
  // ne change la propriété — la montre reste à son propriétaire.
  marchand: ["SALE", "CONSIGNMENT", "CONSIGNMENT_END"],
  expert: ["APPRAISAL", "AUTHENTICATION"],
  assureur: ["INSURANCE"],
  // Pas d'AUTHENTICATION : Provena ne consigne pas d'authentification, le
  // serveur la refuse à un administrateur (RDEC-013).
  admin: ["SERVICE", "SALE", "APPRAISAL", "INSURANCE", "RECOVERY"],
};

export const isPro = (user) =>
  !!user &&
  (user.is_admin ||
    (user.pro_status === "approved" && PRO_ACTOR_VALUES.includes(user.actor_type)));
